import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, MapPin, Briefcase } from "lucide-react";
import { getOpenings, postCareerApplication } from "@/lib/api";

export default function Careers() {
  const [openings, setOpenings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    experience_years: "",
    portfolio_url: "",
    cover_letter: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getOpenings()
      .then((data) => {
        setOpenings(data);
      })
      .catch(() => setOpenings([]));
  }, []);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const apply = (role) => {
    setSelected(role);
    setTimeout(() => {
      document
        .getElementById("apply-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Pick a role", {
        description: "Choose an open position above before submitting.",
      });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        role: selected.title,
        experience_years:
          form.experience_years === "" ? null : parseFloat(form.experience_years),
      };
      await postCareerApplication(payload);
      toast.success("Application received.", {
        description: "We read every one. Expect a reply within 5 business days.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        experience_years: "",
        portfolio_url: "",
        cover_letter: "",
      });
      setSelected(null);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg || "Please check your details.";
      toast.error("Couldn’t submit", { description: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="careers-page">
      <section className="border-b border-bone-300">
        <div className="container-x py-20 sm:py-28">
          <div className="overline">/ Careers</div>
          <h1
            data-testid="careers-heading"
            className="mt-6 font-display font-black text-[clamp(2.5rem,6vw,5.5rem)] leading-[0.95] tracking-tighter text-ink-olive max-w-5xl"
          >
            Small team.
            <span className="font-serif italic font-light"> Serious craft.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ink-soft">
            We hire slowly. If you’re an engineer, designer or operator who
            likes the intersection of engineering and the physical world — you’ll
            like it here.
          </p>
        </div>
      </section>

      <section className="border-b border-bone-300">
        <div className="container-x py-20 sm:py-28">
          <div className="overline">/ Open positions</div>
          <h2 className="mt-4 font-serif italic font-light text-3xl sm:text-4xl text-ink-olive">
            {openings.length} roles open right now.
          </h2>

          <div className="mt-10 divide-y divide-bone-300 border-y border-bone-300">
            {openings.length === 0 && (
              <div
                data-testid="careers-empty"
                className="py-8 text-ink-soft"
              >
                No open roles at the moment. Send us a note anyway.
              </div>
            )}
            {openings.map((role, i) => (
              <div
                key={role.id}
                data-testid={`careers-role-${role.id}`}
                className="grid grid-cols-1 lg:grid-cols-12 gap-4 py-8 items-start hover:bg-white px-4 transition-colors"
              >
                <div className="lg:col-span-2 mono-label text-terracotta pt-1">
                  / 0{i + 1}
                </div>
                <div className="lg:col-span-6">
                  <div className="font-display font-bold text-2xl text-ink-olive">
                    {role.title}
                  </div>
                  <p className="mt-2 text-ink-soft">{role.summary}</p>
                </div>
                <div className="lg:col-span-3 flex flex-col gap-1 text-sm text-ink-soft">
                  <span className="inline-flex items-center gap-1">
                    <Briefcase size={14} /> {role.department} · {role.type}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={14} /> {role.location}
                  </span>
                </div>
                <div className="lg:col-span-1 flex lg:justify-end">
                  <button
                    onClick={() => apply(role)}
                    data-testid={`careers-apply-${role.id}`}
                    className="inline-flex items-center gap-1 border border-ink-olive text-ink-olive px-4 py-2 hover:bg-ink-olive hover:text-bone transition-colors text-sm"
                  >
                    Apply <ArrowUpRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section
        id="apply-form"
        className="border-b border-bone-300 bg-bone-50"
      >
        <div className="container-x py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">/ Application</div>
            <h2 className="mt-4 font-serif italic font-light text-4xl sm:text-5xl text-ink-olive">
              Tell us your story.
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed">
              We prefer a short, honest note over a polished résumé. Share
              links to things you’re proud of.
            </p>
            <p className="mt-4 text-sm text-ink-soft">
              Prefer email? Write to{" "}
              <a
                href="mailto:hr@thiruhanarayanoinnovations.com"
                data-testid="careers-hr-email"
                className="text-navy link-underline font-medium"
              >
                hr@thiruhanarayanoinnovations.com
              </a>
              .
            </p>
            <div
              className="mt-8 border border-bone-300 bg-white p-6"
              data-testid="careers-selected-role"
            >
              <div className="mono-label text-terracotta">/ Selected role</div>
              <div className="mt-2 font-display font-semibold text-xl text-ink-olive">
                {selected ? selected.title : "None selected"}
              </div>
              {selected && (
                <div className="mt-1 text-sm text-ink-soft">
                  {selected.department} · {selected.location}
                </div>
              )}
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 border border-bone-300 bg-white p-8 sm:p-10 space-y-6"
            data-testid="careers-apply-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field
                label="Full name"
                testid="careers-input-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                required
              />
              <Field
                label="Email"
                type="email"
                testid="careers-input-email"
                value={form.email}
                onChange={(v) => update("email", v)}
                required
              />
              <Field
                label="Phone"
                testid="careers-input-phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
              />
              <Field
                label="Years of experience"
                type="number"
                min="0"
                step="0.5"
                testid="careers-input-experience"
                value={form.experience_years}
                onChange={(v) => update("experience_years", v)}
              />
            </div>
            <Field
              label="Portfolio / LinkedIn / GitHub URL"
              testid="careers-input-portfolio"
              value={form.portfolio_url}
              onChange={(v) => update("portfolio_url", v)}
            />
            <div>
              <label className="mono-label text-ink-soft">/ Cover note</label>
              <textarea
                rows={5}
                value={form.cover_letter}
                onChange={(e) => update("cover_letter", e.target.value)}
                data-testid="careers-input-cover"
                className="mt-3 w-full border border-bone-300 bg-white px-4 py-3 font-sans text-ink-olive focus:border-ink-olive focus:outline-none"
                placeholder="What are you excited to work on?"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="careers-submit"
              className="btn-primary disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send application"} <ArrowUpRight size={18} />
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", ...rest }) {
  return (
    <div>
      <label className="mono-label text-ink-soft">/ {label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-3 w-full border border-bone-300 bg-white px-4 py-3 font-sans text-ink-olive focus:border-ink-olive focus:outline-none"
        {...rest}
      />
    </div>
  );
}
